/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  LanguageManager,
  SupportedLanguage,
} from "../../utils/languageManager";
import "./LanguageSelector.scss";

interface LanguageSelectorProps {
  className?: string;
  showLabel?: boolean;
}

export const LanguageSelector: FC<LanguageSelectorProps> = ({
  className = "",
  showLabel = true,
}) => {
  const { i18n } = useTranslation();

  const handleLanguageChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newLanguage = event.target.value as SupportedLanguage;
      console.log(`[LanguageSelector] Changing language to: ${newLanguage}`);

      // Update LanguageManager (Cookie/localStorage)
      LanguageManager.setLanguage(newLanguage);

      // Reload page to ensure all components are re-initialized with new language
      window.location.reload();
    },
    []
  );

  return (
    <div className={`language-selector ${className}`}>
      {showLabel && (
        <label htmlFor="language-select" className="language-selector__label">
          Sprache:
        </label>
      )}
      <select
        id="language-select"
        value={i18n.language}
        onChange={handleLanguageChange}
        className="language-selector__select"
      >
        {LanguageManager.SUPPORTED_LANGUAGES.map((language) => (
          <option key={language} value={language}>
            {LanguageManager.getLanguageDisplayName(language)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LanguageSelector;
